/**
 * SitePick front — `/favorites/` ページ（docs/06 §4）
 *
 * 流れ: localStorage の ID 配列 → 0 件なら通信せず空状態 → 1 件以上なら
 * `POST /sites/batch` → 返った最新データでカード描画。追加順を維持。
 * SOLD / RESERVED は表示のみ変える。確定した欠落だけ localStorage から cleanup
 * （fetch 失敗 / 429 のときは ID を消さない）。
 */

import { favoritesStore } from "./favorites-store.js";
import { compareStore } from "./compare-store.js";
import { fetchBatchSites, SiteApiError } from "./site-api.js";
import { viewItemList, siteToItem } from "./analytics.js";
import { priceBlock } from "./price-block.js";
import { lockNoticeFor } from "./lock-notice.js";
import { holdNoticeText, isHoldState } from "./hold-notice.js";

let pageEl = null;
let listEl = null;
let stateEl = null;
let countEl = null;
let rendering = false;
let queued = false;

function messagesObj() {
  return (window.SITEPICK && window.SITEPICK.messages) || {};
}

function msg(key, fallback) {
  const m = messagesObj();
  return typeof m[key] === "string" && m[key] ? m[key] : fallback;
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) {
    node.className = className;
  }
  if (text !== undefined && text !== null) {
    node.textContent = String(text);
  }
  return node;
}

// 価格の描画は `price-block.js` に集約（商品カード等の PHP 表示と同じマークアップ）。
// Sale 中は SALE ラベル + 通常価格の取り消し線 + セール価格になる。
// **割引の計算はしない** —— サーバー（PriceResolver）が返した値をそのまま出す。
function priceNodeFor(site) {
  return priceBlock(site, msg("priceTbd", "価格準備中"));
}

function statusLabel(status) {
  switch (status) {
    case "AVAILABLE":
      return msg("statusAvailable", "購入可能");
    case "RESERVED":
      return msg("statusReserved", "手続き中");
    case "HELD":
      return msg("statusHeld", "取置き中");
    case "SOLD":
      return msg("statusSold", "SOLD");
    default:
      return msg("statusDraft", "販売前");
  }
}

/**
 * 1 枚のカード DOM。
 *
 * @param {Object} site
 * @returns {HTMLElement}
 */
function renderCard(site) {
  const card = el("article", "c-site-card p-favorites__card");
  card.classList.add(`is-status-${String(site.status || "draft").toLowerCase()}`);
  card.setAttribute("data-sp-id", String(site.id));
  try {
    card.setAttribute(
      "data-sp-item",
      JSON.stringify(siteToItem(site))
    );
  } catch {
    /* optional */
  }

  // メディア
  const media = el("a", "c-site-card__media");
  media.href = site.url || "#";
  if (site.thumb) {
    const img = el("img", "c-site-card__img");
    img.src = site.thumb;
    img.alt = site.name || "";
    img.loading = "lazy";
    img.decoding = "async";
    media.appendChild(img);
  } else {
    media.appendChild(el("span", "c-site-card__img c-site-card__img--placeholder"));
  }
  // カードのラベルは SOLD / NEW のときだけ（parts/site-card.php の sitepick_card_badge() と同じ規則）。
  // 「新着」かどうかはサーバーの `is_new`（ProductState）に従い、JS で日付を計算しない。
  if (site.status === "SOLD") {
    const badge = el("span", "c-site-card__badge");
    badge.appendChild(el("span", "c-badge is-sold", statusLabel("SOLD")));
    media.appendChild(badge);
  } else if (site.is_new === true) {
    const badge = el("span", "c-site-card__badge");
    badge.appendChild(el("span", "c-badge is-new", "NEW"));
    media.appendChild(badge);
  }
  card.appendChild(media);

  // 本文
  const body = el("div", "c-site-card__body");

  const meta = el("p", "c-site-card__meta");
  meta.appendChild(el("span", "c-site-card__no", site.sku || ""));
  if (Array.isArray(site.industry) && site.industry[0]) {
    meta.appendChild(el("span", "c-site-card__industry", site.industry[0].name || ""));
  }
  body.appendChild(meta);

  const title = el("h3", "c-site-card__title");
  const titleLink = el("a", null, site.name || "");
  titleLink.href = site.url || "#";
  title.appendChild(titleLink);
  body.appendChild(title);

  const foot = el("div", "c-site-card__foot");
  const price = el("p", "c-site-card__price");
  if (site.status === "SOLD") {
    price.appendChild(el("span", "c-site-card__sold", msg("soldByOther", "他社が購入しました")));
  } else {
    price.appendChild(priceNodeFor(site));
  }
  foot.appendChild(price);

  // 一覧カード（parts/site-card.php）と同じ導線・同じ見た目に揃える。
  // 「詳細を見る」= 線のボタン、「実物を見る」= 塗りのボタン（docs/05 の CTA 優先度）。
  const actions = el("p", "c-site-card__actions");
  const detailLink = el("a", "c-button c-button--secondary c-button--sm c-site-card__detail", msg("viewDetail", "詳細を見る"));
  detailLink.href = site.url || "#";
  actions.appendChild(detailLink);
  const liveLink = el("a", "c-button c-button--sm c-site-card__preview", msg("viewLive", "実物を見る"));
  liveLink.href = site.live_url || site.url || "#";
  actions.appendChild(liveLink);
  foot.appendChild(actions);

  // 購入 lock 中（RESERVED）の補助メッセージ。parts/site-card.php と同じ位置・同じ文。
  // 残り分はサーバー（ProductState → REST `reserved_minutes_left`）の値をそのまま出す。
  // ここで reserved_until から計算したり、状態を独自に判断したりしない（バッジにもしない）。
  // 取置き（HELD、または RESERVED + hold_active）は「取置き中（あと約 N 時間）」。「約 N 分後」は出さない。
  if (site.status === "HELD" || isHoldState(site)) {
    foot.appendChild(el("p", "c-site-card__lock", holdNoticeText(site, messagesObj(), "card")));
  } else if (site.status === "RESERVED") {
    foot.appendChild(el("p", "c-site-card__lock", lockNoticeFor(site)));
  }
  body.appendChild(foot);

  if (site.status === "SOLD") {
    const note = el("p", "p-favorites__unavailable", msg("favUnavailableSold", "このサイトは他社が購入しました。"));
    body.appendChild(note);
    if (Array.isArray(site.industry) && site.industry[0] && site.industry[0].slug) {
      const rel = el("a", "c-link", msg("seeSimilar", "同じ業種のサイトを見る"));
      rel.href = `${(window.SITEPICK && window.SITEPICK.urls && window.SITEPICK.urls.sites) || "/sites/"}?industry=${encodeURIComponent(site.industry[0].slug)}`;
      body.appendChild(rel);
    }
  }

  // お気に入り解除 / 比較追加（既存クラスを利用）
  const utils = el("div", "c-site-card__utils");
  const fav = el("button", "c-favorite-button js-sp-favorite is-active");
  fav.type = "button";
  fav.setAttribute("aria-pressed", "true");
  fav.setAttribute("data-sp-id", String(site.id));
  fav.appendChild(el("span", "u-visually-hidden", msg("favoriteRemoveAction", "お気に入りから削除")));
  fav.appendChild(el("span", null, "♡")).setAttribute("aria-hidden", "true");
  utils.appendChild(fav);

  const cmp = el("button", "c-compare-button js-sp-compare");
  cmp.type = "button";
  cmp.setAttribute("aria-pressed", compareStore.has(site.id) ? "true" : "false");
  cmp.classList.toggle("is-active", compareStore.has(site.id));
  cmp.setAttribute("data-sp-id", String(site.id));
  cmp.appendChild(el("span", "u-visually-hidden", msg("compareAddAction", "比較に追加")));
  cmp.appendChild(el("span", null, "⇄")).setAttribute("aria-hidden", "true");
  utils.appendChild(cmp);

  body.appendChild(utils);
  card.appendChild(body);
  return card;
}

function showState(name, message) {
  if (!stateEl) {
    return;
  }
  stateEl.hidden = false;
  stateEl.dataset.spState = name;
  const text = stateEl.querySelector("[data-sp-state-text]");
  if (text && message) {
    text.textContent = message;
  }
  const retry = stateEl.querySelector("[data-sp-retry]");
  if (retry) {
    retry.hidden = name !== "error";
  }
}

function hideState() {
  if (stateEl) {
    stateEl.hidden = true;
    stateEl.dataset.spState = "";
  }
}

function setCount(n) {
  if (countEl) {
    countEl.textContent = String(n);
  }
}

/**
 * メイン描画。
 */
async function render() {
  if (rendering) {
    queued = true;
    return;
  }
  rendering = true;

  const ids = favoritesStore.get();
  setCount(ids.length);

  if (ids.length === 0) {
    if (listEl) {
      listEl.textContent = "";
    }
    showState("empty", msg("favEmpty", "お気に入りに追加したサイトはありません。"));
    rendering = false;
    return;
  }

  showState("loading", msg("loading", "読み込み中…"));

  let sites;
  try {
    sites = await fetchBatchSites(ids);
  } catch (err) {
    // 失敗時は ID を消さない。
    showState(
      "error",
      err instanceof SiteApiError && err.kind === "rate_limited"
        ? msg("rateLimited", "アクセスが集中しています。しばらくして再度お試しください。")
        : msg("error", "エラーが発生しました。時間をおいて再度お試しください。")
    );
    rendering = false;
    if (queued) {
      queued = false;
      render();
    }
    return;
  }

  // 追加順を維持して並べる。
  const byId = new Map();
  sites.forEach((s) => {
    if (s && Number.isInteger(s.id)) {
      byId.set(s.id, s);
    }
  });
  const ordered = ids.filter((id) => byId.has(id)).map((id) => byId.get(id));

  // 確定した欠落のみ cleanup（fetch は成功している）。
  const keep = ids.filter((id) => byId.has(id));
  if (keep.length !== ids.length) {
    favoritesStore.retainOnly(keep);
  }

  if (listEl) {
    listEl.textContent = "";
    ordered.forEach((site) => listEl.appendChild(renderCard(site)));
  }

  if (ordered.length === 0) {
    showState("empty", msg("favEmpty", "お気に入りに追加したサイトはありません。"));
  } else {
    hideState();
    viewItemList(
      "favorites",
      msg("favListName", "お気に入り"),
      ordered.map((s, i) => siteToItem(s, i + 1))
    );
  }

  rendering = false;
  if (queued) {
    queued = false;
    render();
  }
}

/**
 * 初期化。
 *
 * @returns {void}
 */
export function initFavoritesPage() {
  pageEl = document.querySelector('[data-sp-page="favorites"]');
  if (!pageEl) {
    return;
  }
  listEl = pageEl.querySelector("[data-sp-favorites-list]");
  stateEl = pageEl.querySelector("[data-sp-favorites-state]");
  countEl = pageEl.querySelector("[data-sp-favorites-count]");

  const retry = pageEl.querySelector("[data-sp-retry]");
  if (retry) {
    retry.addEventListener("click", () => render());
  }

  favoritesStore.subscribe(() => render());
  render();
}
